import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import type { Observable, OperatorFunction } from 'rxjs';
import { catchError, from, map, mergeMap, switchMap, throwError } from 'rxjs';
import type { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { ajax } from 'rxjs/ajax';
import { fromFetch } from 'rxjs/fetch';
import { type HttpClientPreInterceptor, provideHttpClientPreInterceptor } from './http-client-pre-interceptor';
import type { HttpOptions, HttpVerboseOptions, SSRHttpOptions } from './types';

export type HttpError = {
  readonly _type: '_HttpError';
  readonly url: string;
  readonly status: number;
  readonly statusText: string;
  readonly error: Record<string, any> | undefined;
  readonly errorAsString: string | undefined;
  readonly responseHeaders: Record<string, string>;
};

type Mappers<T> = [(json: any) => T, <R>(errorJson: any) => R] | [(json: any) => T];

async function extractJson(responseText: string): Promise<Record<string, any> | undefined> {
  try {
    return JSON.parse(responseText);
  } catch (_err) {
    return undefined;
  }
}

export async function createHttpError(response: Response): Promise<HttpError> {
  const responseHeaders: Record<string, string> = {};

  response.headers.forEach((value: string, key: string) => {
    responseHeaders[key] = value;
  });

  const textResponse: string = await response.text();
  return {
    _type: '_HttpError',
    url: response.url,
    status: response.status,
    statusText: response.statusText,
    error: await extractJson(textResponse),
    errorAsString: textResponse,
    responseHeaders: responseHeaders
  };
}

export function createHttpErrorFromFailedToFetch(url: string, err: Error): HttpError | Error {
  const isFailedToFetch: boolean = Boolean(err && err.message && err.message.toLowerCase().includes('failed to fetch'));
  const isAborted: boolean = Boolean(err && err.name === 'AbortError');
  if (!isFailedToFetch && !isAborted) {
    return err;
  }
  return {
    _type: '_HttpError',
    url: url,
    status: 0,
    // Carries the request URL through to Sentry's existing HttpError formatting
    // (sentry-serialized-rejection-fingerprint.ts) instead of leaking a bare, origin-less
    // native AbortError when the browser cancels an in-flight request (e.g. tab backgrounded
    // mid-request). See BCF-V2-PREVIEW-4P.
    statusText: isAborted ? `AbortError: ${err.message}` : 'failed to fetch',
    error: undefined,
    errorAsString: undefined,
    responseHeaders: {}
  };
}

export function isHttpError(error: any): error is HttpError {
  return error && error._type === '_HttpError';
}

export class HttpClient {
  constructor(protected _preInterceptor: HttpClientPreInterceptor) {}

  public get<T extends object>(url: string, options?: HttpOptions, _ssrOptions?: SSRHttpOptions): Observable<T> {
    const preIntercept$: Observable<Response> | undefined = this._preInterceptor.doPreIntercept({
      url: url,
      method: 'GET',
      httpOptions: options
    });
    if (preIntercept$) {
      return preIntercept$.pipe(
        this._handleResponse<T>(),
        catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
      );
    }
    return fromFetch(url, {
      ...options,
      method: 'GET',
      headers: {
        ...(options?.headers ?? {})
      }
    }).pipe(
      this._handleResponse<T>(),
      catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
    );
  }

  public getMapped<T extends object>(url: string, mappers: Mappers<T>, options?: HttpOptions): Observable<T> {
    const preIntercept$: Observable<Response> | undefined = this._preInterceptor.doPreIntercept({
      url: url,
      method: 'GET',
      httpOptions: options
    });
    if (preIntercept$) {
      return preIntercept$.pipe(
        this._handleResponse<T>(mappers),
        catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
      );
    }
    return fromFetch(url, {
      ...options,
      method: 'GET',
      headers: {
        ...(options?.headers ?? {})
      }
    }).pipe(
      this._handleResponse<T>(mappers),
      catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
    );
  }

  public getAsText(url: string, options?: HttpOptions): Observable<string> {
    const preIntercept$: Observable<Response> | undefined = this._preInterceptor.doPreIntercept({
      url: url,
      method: 'GET',
      httpOptions: options
    });
    if (preIntercept$) {
      return preIntercept$.pipe(
        this._handleTextResponse(),
        catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
      );
    }
    return fromFetch(url, {
      ...options,
      method: 'GET',
      headers: {
        ...(options?.headers ?? {})
      }
    }).pipe(
      this._handleTextResponse(),
      catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
    );
  }

  public delete(url: string, options?: HttpOptions): Observable<string> {
    return fromFetch(url, {
      ...options,
      method: 'DELETE',
      headers: {
        ...(options?.headers ?? {})
      }
    }).pipe(
      this._handleTextResponse(),
      catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
    );
  }
  public post<T>(url: string, body: FormData, options?: HttpOptions): Observable<T>;
  public post<T>(url: string, body: object, options?: HttpOptions): Observable<T>;
  public post<T>(
    url: string,
    body: object | FormData,
    options?: HttpVerboseOptions
  ): Observable<T> | Observable<AjaxResponse<T>> {
    if (body instanceof FormData) {
      if (options && options.reportProgress && 'reportProgress' in options) {
        return uploadFile<T>(url, options, body).pipe(
          catchError((err: AjaxError) => {
            const ajaxToResponseMetadata: Response = mapAjaxErrorToResponse(err);
            return from(createHttpError(ajaxToResponseMetadata)).pipe(
              mergeMap((error: HttpError) => throwError(() => error))
            );
          })
        );
      }

      return fromFetch(url, {
        ...options,
        method: 'POST',
        body: body,
        headers: {
          ...(options?.headers ?? {})
        }
      }).pipe(
        this._handleResponse<T>(),
        catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
      );
    }

    return fromFetch(url, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers ?? {})
      },
      body: JSON.stringify(body)
    }).pipe(
      this._handleResponse<T>(),
      catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
    );
  }

  public put<T>(url: string, body: FormData, options?: HttpOptions): Observable<T>;
  public put<T>(url: string, body: object, options?: HttpOptions): Observable<T>;
  public put<T>(url: string, body: object | FormData, options?: HttpOptions): Observable<T> {
    if (body instanceof FormData) {
      return fromFetch(url, {
        ...options,
        method: 'PUT',
        body: body,
        headers: {
          ...(options?.headers ?? {})
        }
      }).pipe(
        this._handleResponse<T>(),
        catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
      );
    }

    return fromFetch(url, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers ?? {})
      },
      body: JSON.stringify(body)
    }).pipe(
      this._handleResponse<T>(),
      catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
    );
  }

  public options<T>(url: string, body: object | undefined = undefined, options?: HttpOptions): Observable<T> {
    return fromFetch(url, {
      ...options,
      method: 'Options',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers ?? {})
      },
      body: body ? JSON.stringify(body) : undefined
    }).pipe(
      this._handleResponse<T>(),
      catchError((err: Error) => throwError(() => createHttpErrorFromFailedToFetch(url, err)))
    );
  }

  protected _handleResponse<T>(mappers?: Mappers<T>): OperatorFunction<Response, T> {
    return switchMap((response: Response) => {
      if (!response.ok) {
        return from(createHttpError(response)).pipe(mergeMap((error: HttpError) => throwError(() => error)));
      }

      return from(response.text()).pipe(
        map((text: string) => {
          if (!text) return null as T;

          const json: any = JSON.parse(text);
          return mappers ? mappers[0](json) : json;
        })
      );
    });
  }

  private _handleTextResponse(): OperatorFunction<Response, string> {
    return switchMap((response: Response) => {
      if (response.ok) {
        return response.text();
      } else {
        return from(createHttpError(response)).pipe(
          mergeMap((error: HttpError) => {
            return throwError(() => error);
          })
        );
      }
    });
  }
}

export function provideHttpClient(): HttpClient {
  return provideSingleton(HttpClient, () => new HttpClient(provideHttpClientPreInterceptor()));
}

export function uploadFile<T>(url: string, options: HttpOptions, formData: FormData): Observable<AjaxResponse<T>> {
  return ajax<T>({
    url,
    method: 'POST',
    body: formData,
    headers: {
      ...(options.headers ?? {})
    },
    responseType: 'json',
    includeUploadProgress: true,
    withCredentials: true
  });
}

export function mapAjaxErrorToResponse(err: AjaxError): Response {
  const body: string = typeof err.response === 'string' ? err.response : JSON.stringify(err.response);

  const headers: Headers = new Headers();
  const rawHeaders: string[] = err.xhr
    .getAllResponseHeaders()
    .trim()
    .split(/[\r\n]+/);
  for (const line of rawHeaders) {
    // biome-ignore lint/plugin/typedef: replace eslint/typedef
    const [key, ...values] = line.split(': ');
    headers.append(key, values.join(': '));
  }

  return new Response(body, {
    status: err.status || 0,
    statusText: err.xhr.statusText || err.message,
    headers
  });
}
